/**
 * Series vs Parallel concept model — topology from physical jumper connections.
 *
 * Pure TypeScript (no Three.js). Environment / hands snap jumper endpoints onto
 * stable {@link NodeId} terminals and call {@link evaluateSeriesParallel}.
 *
 * Framing: conceptual non-energized mockup values only. Messy graphs are `open`
 * (lamps off) — not a SPICE solver.
 */

import { CONCEPT_VOLTAGE_V } from "./ohmsLaw";

/** Stable terminal ids environment/hands use for jumper snaps. */
export const SERIES_PARALLEL_NODES = [
  "srcP",
  "srcN",
  "aP",
  "aN",
  "bP",
  "bN",
] as const;

export type NodeId = (typeof SERIES_PARALLEL_NODES)[number];

const NODE_ID_SET: ReadonlySet<string> = new Set(SERIES_PARALLEL_NODES);

export function isSeriesParallelNodeId(id: string): id is NodeId {
  return NODE_ID_SET.has(id);
}

/** Equal lamp resistance (Ω). 24 Ω keeps V=12 numbers readable (0.25 A / 0.5 A). */
export const LAMP_RESISTANCE_OHMS = 24;

/** Undirected jumper lead — two terminal endpoints. */
export type Connection = [NodeId, NodeId];

export type TopologyKind = "open" | "series" | "parallel";

/** Conceptual DC source between {@link NodeId} terminals. */
export interface ConceptSource {
  readonly positive: NodeId;
  readonly negative: NodeId;
  readonly voltageV: number;
}

/** Resistive load spanning two terminals. */
export interface ConceptLoad {
  readonly id: "lampA" | "lampB";
  readonly positive: NodeId;
  readonly negative: NodeId;
  readonly resistanceOhms: number;
}

export const SERIES_PARALLEL_SOURCE: ConceptSource = {
  positive: "srcP",
  negative: "srcN",
  voltageV: CONCEPT_VOLTAGE_V,
};

export const LAMP_A: ConceptLoad = {
  id: "lampA",
  positive: "aP",
  negative: "aN",
  resistanceOhms: LAMP_RESISTANCE_OHMS,
};

export const LAMP_B: ConceptLoad = {
  id: "lampB",
  positive: "bP",
  negative: "bN",
  resistanceOhms: LAMP_RESISTANCE_OHMS,
};

export const SERIES_PARALLEL_LOADS: readonly ConceptLoad[] = [LAMP_A, LAMP_B];

/** Per-lamp outputs for render / HUD. */
export interface LampElectricalState {
  readonly currentA: number;
  readonly voltageV: number;
  /** Glow in [0, 1] — lamp voltage / source voltage. */
  readonly brightness: number;
}

/** Evaluated series/parallel (or open) circuit. */
export interface SeriesParallelState {
  /** Conceptual source voltage (V). */
  readonly voltageV: number;
  readonly topology: TopologyKind;
  /** Source current (A). */
  readonly totalCurrentA: number;
  readonly lampA: LampElectricalState;
  readonly lampB: LampElectricalState;
}

const LAMP_A_EDGE: readonly [NodeId, NodeId] = ["aP", "aN"];
const LAMP_B_EDGE: readonly [NodeId, NodeId] = ["bP", "bN"];

function clamp01(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return t;
}

function lampState(voltageV: number, currentA: number, sourceV: number): LampElectricalState {
  return {
    currentA,
    voltageV,
    brightness: sourceV > 0 ? clamp01(voltageV / sourceV) : 0,
  };
}

function offLamp(): LampElectricalState {
  return lampState(0, 0, CONCEPT_VOLTAGE_V);
}

function openState(): SeriesParallelState {
  return {
    voltageV: CONCEPT_VOLTAGE_V,
    topology: "open",
    totalCurrentA: 0,
    lampA: offLamp(),
    lampB: offLamp(),
  };
}

type Adj = Map<NodeId, Set<NodeId>>;

function emptyAdj(): Adj {
  const adj: Adj = new Map();
  for (const id of SERIES_PARALLEL_NODES) {
    adj.set(id, new Set());
  }
  return adj;
}

function addUndirected(adj: Adj, a: NodeId, b: NodeId): void {
  if (a === b) return;
  adj.get(a)!.add(b);
  adj.get(b)!.add(a);
}

function buildAdj(
  jumpers: Array<[NodeId, NodeId]>,
  loads: ReadonlyArray<readonly [NodeId, NodeId]>,
): Adj {
  const adj = emptyAdj();
  for (const pair of jumpers) {
    const a = pair[0];
    const b = pair[1];
    if (!isSeriesParallelNodeId(a) || !isSeriesParallelNodeId(b)) continue;
    addUndirected(adj, a, b);
  }
  for (const [a, b] of loads) {
    addUndirected(adj, a, b);
  }
  return adj;
}

function isReachable(adj: Adj, start: NodeId, goal: NodeId): boolean {
  if (start === goal) return true;
  const seen = new Set<NodeId>([start]);
  const queue: NodeId[] = [start];
  for (let i = 0; i < queue.length; i++) {
    const cur = queue[i]!;
    for (const next of adj.get(cur) ?? []) {
      if (seen.has(next)) continue;
      if (next === goal) return true;
      seen.add(next);
      queue.push(next);
    }
  }
  return false;
}

function sourceReturnConnected(
  jumpers: Array<[NodeId, NodeId]>,
  loads: ReadonlyArray<readonly [NodeId, NodeId]>,
): boolean {
  const { positive, negative } = SERIES_PARALLEL_SOURCE;
  return isReachable(buildAdj(jumpers, loads), positive, negative);
}

/**
 * Classify jumper topology, then apply textbook two-equal-lamp Ohm’s law.
 *
 * Detection (source poles `srcP` / `srcN`; lamps are always aP–aN and bP–bN):
 * - `open` — source shorted by jumpers, no source-to-return path, only one lamp
 *   in circuit, or any other mess. Lamps off.
 * - `series` — path exists only when *both* lamps are included (A then B or
 *   B then A). No jumper bypass around either lamp. I = V/(2R), each lamp V/2.
 * - `parallel` — each lamp independently bridges source to return (either
 *   polarity). Each lamp sees V; total I = 2V/R.
 *
 * Duplicate jumpers and self-loops are ignored. Unknown ids are skipped.
 */
export function evaluateSeriesParallel(
  connections: Array<[NodeId, NodeId]>,
): SeriesParallelState {
  const v = CONCEPT_VOLTAGE_V;
  const r = LAMP_RESISTANCE_OHMS;

  const shorted = sourceReturnConnected(connections, []);
  const viaBoth = sourceReturnConnected(connections, [LAMP_A_EDGE, LAMP_B_EDGE]);
  const viaA = sourceReturnConnected(connections, [LAMP_A_EDGE]);
  const viaB = sourceReturnConnected(connections, [LAMP_B_EDGE]);

  if (shorted || !viaBoth) {
    return openState();
  }

  if (viaA && viaB) {
    const lampI = v / r;
    const lamp = lampState(v, lampI, v);
    return {
      voltageV: v,
      topology: "parallel",
      totalCurrentA: 2 * lampI,
      lampA: lamp,
      lampB: lamp,
    };
  }

  if (!viaA && !viaB) {
    const seriesI = v / (2 * r);
    const lampV = seriesI * r;
    const lamp = lampState(lampV, seriesI, v);
    return {
      voltageV: v,
      topology: "series",
      totalCurrentA: seriesI,
      lampA: lamp,
      lampB: lamp,
    };
  }

  // Single lamp (or other incomplete teaching circuit) → lamps off.
  return openState();
}
